import React, { createContext, useContext, useState, useEffect } from 'react';
import { publicConfigError, supabase } from '../lib/supabaseClient';

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!supabase) {
            setError(publicConfigError);
            setLoading(false);
            return undefined;
        }

        // Helper for background tasks (IP logs, email sync)
        const runSideEffects = async (currentUser) => {
            if (!currentUser) return;
            const withTimeout = (p, ms) => Promise.race([p, new Promise((_, r) => setTimeout(() => r(new Error('timeout')), ms))]);

            try {
                const ipRes = await fetch('/api/ip-info');
                const ipData = await ipRes.json();

                // Validate data before inserting to avoid "undefined" strings
                const logData = {
                    user_id: currentUser.id,
                    ip_address: ipData.ip || '0.0.0.0',
                    location: (ipData.city && ipData.country_name) ? `${ipData.city}, ${ipData.country_name}` : 'Localisation inconnue',
                    isp: ipData.org || 'ISP inconnu',
                    user_agent: navigator.userAgent
                };

                // Insert log with timeout — in background
                withTimeout(supabase.from('access_logs').insert(logData), 3000)
                    .catch(e => console.warn('[Auth] Access log insert timed out or failed:', e.message));

                // Update profile email with timeout — in background
                if (currentUser.email) {
                    withTimeout(supabase.from('profiles')
                        .update({ email: currentUser.email })
                        .eq('user_id', currentUser.id), 3000
                    ).catch(e => console.warn('[Auth] Email sync timed out or failed:', e.message));
                }
            } catch (err) {
                console.warn("[Auth] Side effects failed (non-critical):", err.message);
            }
        };

        // Check active sessions and sets the user
        const getSession = async () => {
            try {
                console.log('[Auth] getSession started');
                const { data: { session }, error: sessionError } = await supabase.auth.getSession();
                if (sessionError) throw sessionError;

                const currentUser = session?.user ?? null;
                console.log('[Auth] getSession completed. User:', currentUser?.id);
                setUser(currentUser);
                setError(null);

                if (currentUser) runSideEffects(currentUser);
            } catch (sessionError) {
                console.error('[Auth] Failed to restore session:', sessionError);
                setError('Connexion au compte indisponible pour le moment.');
            } finally {
                setLoading(false);
            }
        };

        getSession();

        // Listen for changes on auth state (sign in, sign out, etc.)
        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
            const currentUser = session?.user ?? null;
            setUser(currentUser);
            setLoading(false);
            setError(null);

            if (event === 'SIGNED_IN' && currentUser) {
                runSideEffects(currentUser);
            }
        });

        return () => subscription.unsubscribe();
    }, []);

    const value = {
        user,
        loading,
        error,
        signOut: () => {
            if (!supabase) return Promise.resolve();
            return supabase.auth.signOut();
        },
        updatePassword: async (password) => {
            if (!supabase) throw new Error(publicConfigError);
            const { data, error } = await supabase.auth.updateUser({ password });
            if (error) throw error;
            setUser(data.user);
            return { success: true };
        },
        updateUserMetadata: async (metadata) => {
            if (!supabase) throw new Error(publicConfigError);
            const { data, error } = await supabase.auth.updateUser({ data: metadata });
            if (error) throw error;
            setUser(data.user);
            return { success: true };
        }
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
